import React from 'react';
import { brand, forAudience, Service } from '../../config/brand';
import { useReveal } from '../../hooks/useReveal';
import { useAudience } from '../Audience/AudienceContext';
import AudienceSwitch from '../Audience/AudienceSwitch';
import SplitText from '../SplitText';
import './Services.css';

const Services: React.FC = () => {
  const ref = useReveal();
  const { audience } = useAudience();

  // Ranked for the selected track — the top three lead as full cards, the
  // rest fall through to the compact "also capable of" list.
  const ranked = forAudience(brand.services as Service[], audience);
  const featured = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  return (
    <section className="services section" id="services" aria-labelledby="services-heading" ref={ref}>
      <div className="container">
        <div className="section-head">
          <div>
            <span className="section-label">What We Build</span>
            <h2 className="section-title reveal" id="services-heading">
              <SplitText
                text={"We specialise in agentic AI,\nfrom prototype to production"}
                highlight="agentic AI,"
              />
            </h2>
            <p className="section-subtitle reveal reveal-delay-1">
              We don't consult — we build, deploy and scale. Every engagement ends with a working
              system in production and someone on call for it.
            </p>
          </div>
          <div className="services__filter reveal reveal-delay-1">
            <AudienceSwitch id="services-track" label="Filter services by track" />
          </div>
        </div>

        <div className="services__grid">
          {featured.map((service, i) => (
            <article
              className={`services__card card reveal reveal-delay-${Math.min(i + 1, 6)}`}
              key={service.title}
              aria-label={service.title}
            >
              {/* Outcome leads; the technical detail is the supporting line */}
              <h3 className="services__card-title">{service.title}</h3>
              <p className="services__card-outcome">{service.outcome}</p>
              <p className="services__card-desc">{service.description}</p>

              <div className="services__card-tags">
                {service.tags.slice(0, 4).map((tag) => (
                  <span className="tag" key={tag}>{tag}</span>
                ))}
              </div>
            </article>
          ))}
        </div>

        {rest.length > 0 && (
          <div className="services__more reveal">
            <span className="services__more-label">Also capable of</span>
            <div className="services__more-grid">
              {rest.map((service) => (
                <div className="services__more-item" key={service.title}>
                  <span className="services__more-title">{service.title}</span>
                  <span className="services__more-desc">{service.outcome}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
